import { z } from 'zod';
import { CareDocumentType, CaseReferenceSource } from '../../enums';
import {
  CareDocumentsSettings,
  CareDocumentsSettingsSchema,
} from './CareDocumentsSettingsSchema';
import {
  SupportLevelsSettings,
  SupportLevelsSettingsSchema,
} from './SupportLevelsSettingsSchema';
import {
  SlackNotificationsSettings,
  SlackNotificationsSettingsSchema,
} from './SlackNotificationsSettingsSchema';
import {
  CaseWorkflowsSettings,
  CaseWorkflowsSettingsSchema,
} from './CaseWorkflowsSettingsSchema';
import {
  ReferralAutoCancellationSettings,
  ReferralAutoCancellationSettingsSchema,
} from './ReferralAutoCancellationSettingsSchema';
import {
  ReferralFormSettings,
  ReferralFormSettingsSchema,
} from './ReferralFormSettingsSchema';
import {
  ReferralPausingSettings,
  ReferralPausingSettingsSchema,
} from './ReferralPausingSettingsSchema';
import {
  PreAuthorizationRequestForm,
  PreAuthorizationSettings,
  PreAuthorizationSettingsSchema,
} from './PreAuthorizationSettingsSchema';

// Reserved id of the "Default settings" node — the editable defaults for ALL
// organizations, stored at /organizations/root/settings/{groupId}.
// The `organizations/root` document itself never exists (organizations are
// platform-owned); only its `settings` subcollection is used.
export const ROOT_ORGANIZATION_SETTINGS_ID = 'root';

// How the reserved node is presented in the UI. Every organization inherits
// from it unless a nearer ancestor overrides a group.
export const ROOT_ORGANIZATION_SETTINGS_DISPLAY_NAME = 'Default settings';

// Settings group ids — one member per group doc. The enum VALUE is both the
// Firestore doc id and the key of OrganizationSettingsValues, so resolved
// settings keep plain dot access (settings.careDocuments) and doc id ===
// interface key === enum value.
export enum OrganizationSettingsGroupId {
  careDocuments = 'careDocuments',
  supportLevels = 'supportLevels',
  preAuthorization = 'preAuthorization',
  slackNotifications = 'slackNotifications',
  caseWorkflows = 'caseWorkflows',
  referralAutoCancellation = 'referralAutoCancellation',
  referralPausing = 'referralPausing',
  referralForm = 'referralForm',
}

// The resolved values of every settings group.
export interface OrganizationSettingsValues {
  [OrganizationSettingsGroupId.careDocuments]: CareDocumentsSettings;
  [OrganizationSettingsGroupId.supportLevels]: SupportLevelsSettings;
  [OrganizationSettingsGroupId.preAuthorization]: PreAuthorizationSettings;
  [OrganizationSettingsGroupId.slackNotifications]: SlackNotificationsSettings;
  [OrganizationSettingsGroupId.caseWorkflows]: CaseWorkflowsSettings;
  [OrganizationSettingsGroupId.referralAutoCancellation]: ReferralAutoCancellationSettings;
  [OrganizationSettingsGroupId.referralPausing]: ReferralPausingSettings;
  [OrganizationSettingsGroupId.referralForm]: ReferralFormSettings;
}

// Doc metadata excluded from resolution: audit fields stamped by the API on
// write, plus the doc id injected by normalizeDoc (equals the group id).
export interface OrganizationSettingsAudit {
  id?: string;
  updatedAt?: Date;
  updatedBy?: string;
}

export type StoredOrganizationSettings<K extends OrganizationSettingsGroupId> =
  OrganizationSettingsValues[K] & OrganizationSettingsAudit;

// Code-level defaults — the bottom layer of resolution, used when neither
// the organization chain nor the "(root) settings" define a group.
// They deliberately equal the behavior of a plain (non-partner) organization.
export const organizationSettingsDefaults: OrganizationSettingsValues = {
  careDocuments: {
    types: [CareDocumentType.PersonalisedCarePlan],
    caseReferenceSource: CaseReferenceSource.none,
    careForecast: false,
    careForecastDurations: [],
    careForecastRtwNotExpectedWithin: null,
    canSendToCaseManager: false,
    caseManagerCcEmails: [],
    caseManagerCcMemberTypes: [],
    caseManagerCcEmailsByType: [],
    canSendToNurse: false,
    nurseEmail: '',
  },
  supportLevels: {
    enabled: false,
    levels: [],
    defaultLevel: null,
    displayLevel: true,
  },
  preAuthorization: {
    requestForm: PreAuthorizationRequestForm.none,
    requestFormUrl: '',
    recordsOutcome: false,
  },
  slackNotifications: {
    appointmentAlerts: false,
    customerSupportReferralsChannel: '',
    customerSupportCasesChannel: '',
    customerSupportPatientConsentCallsChannel: '',
  },
  caseWorkflows: {
    mdtFeedback: false,
    canDischarge: false,
    rtwSeForms: false,
    rtwOutcomeTracking: false,
    appointmentConclusions: false,
  },
  // A plain organization has no scheduled auto-cancellation jobs...
  referralAutoCancellation: { cancelSentAfterDays: null },
  // ...and its referrals can be paused (today's behavior for every referral),
  // with no scheduled cancellation of paused referrals.
  referralPausing: { canPause: true, cancelPausedAfterDays: null },
  // The referral form shows no optional fields by default.
  referralForm: { showPatientConsentPhoneCall: false },
};

export const organizationSettingsGroups: {
  [K in OrganizationSettingsGroupId]: {
    title: string;
    schema: z.ZodType;
  };
} = {
  careDocuments: {
    title: 'Care documents',
    schema: CareDocumentsSettingsSchema,
  },
  supportLevels: {
    title: 'Support levels',
    schema: SupportLevelsSettingsSchema,
  },
  preAuthorization: {
    title: 'Pre-authorisation',
    schema: PreAuthorizationSettingsSchema,
  },
  slackNotifications: {
    title: 'Slack notifications',
    schema: SlackNotificationsSettingsSchema,
  },
  caseWorkflows: {
    title: 'Case workflows',
    schema: CaseWorkflowsSettingsSchema,
  },
  referralAutoCancellation: {
    title: 'Referral auto-cancellation',
    schema: ReferralAutoCancellationSettingsSchema,
  },
  referralPausing: {
    title: 'Referral pausing',
    schema: ReferralPausingSettingsSchema,
  },
  referralForm: {
    title: 'Referral form',
    schema: ReferralFormSettingsSchema,
  },
};

export const organizationSettingsGroupIds = Object.values(
  OrganizationSettingsGroupId,
);

// Formats group values BEFORE validation so every writer (API, migrations)
// stores the same shape. Email fields are kept lowercase without spaces;
// blank list entries are dropped; the pre-authorisation form URL is trimmed.
// Non-string entries pass through untouched
// so schema validation still rejects them.
export function formatOrganizationSettingsGroupValues(
  groupId: OrganizationSettingsGroupId,
  values: unknown,
): unknown {
  if (
    groupId === OrganizationSettingsGroupId.careDocuments &&
    values !== null &&
    typeof values === 'object'
  ) {
    const formatted = { ...(values as Record<string, unknown>) };
    if (Array.isArray(formatted.caseManagerCcEmails)) {
      formatted.caseManagerCcEmails = formatted.caseManagerCcEmails
        .map((email) =>
          typeof email === 'string'
            ? email.replace(/\s+/g, '').toLowerCase()
            : email,
        )
        .filter((email) => email !== '');
    }
    if (Array.isArray(formatted.caseManagerCcEmailsByType)) {
      // format each entry's emails like caseManagerCcEmails; entries left
      // with no emails are dropped (like blank rows) — the schema then
      // requires every stored entry to be non-empty. Malformed entries pass
      // through untouched so schema validation still rejects them.
      formatted.caseManagerCcEmailsByType = formatted.caseManagerCcEmailsByType
        .map((entry) => {
          if (
            entry === null ||
            typeof entry !== 'object' ||
            !Array.isArray((entry as { emails?: unknown }).emails)
          ) {
            return entry;
          }
          return {
            ...(entry as Record<string, unknown>),
            emails: (entry as { emails: unknown[] }).emails
              .map((email) =>
                typeof email === 'string'
                  ? email.replace(/\s+/g, '').toLowerCase()
                  : email,
              )
              .filter((email) => email !== ''),
          };
        })
        .filter(
          (entry) =>
            !(
              entry !== null &&
              typeof entry === 'object' &&
              Array.isArray((entry as { emails?: unknown }).emails) &&
              (entry as { emails: unknown[] }).emails.length === 0
            ),
        );
    }
    if (typeof formatted.nurseEmail === 'string') {
      formatted.nurseEmail = formatted.nurseEmail
        .replace(/\s+/g, '')
        .toLowerCase();
    }
    if (Array.isArray(formatted.careForecastDurations)) {
      // a blank duration row in the admin editor carries value 0 — dropped
      // like blank email rows; anything else malformed still reaches the
      // schema and is rejected there
      formatted.careForecastDurations = formatted.careForecastDurations.filter(
        (duration) =>
          !(
            duration !== null &&
            typeof duration === 'object' &&
            (duration as { value?: unknown }).value === 0
          ),
      );
    }
    return formatted;
  }
  if (
    groupId === OrganizationSettingsGroupId.preAuthorization &&
    values !== null &&
    typeof values === 'object'
  ) {
    const formatted = { ...(values as Record<string, unknown>) };
    if (typeof formatted.requestFormUrl === 'string') {
      formatted.requestFormUrl = formatted.requestFormUrl.trim();
    }
    return formatted;
  }
  return values;
}

export function isOrganizationSettingsGroupId(
  groupId: string,
): groupId is OrganizationSettingsGroupId {
  // hasOwnProperty (not `in`): `in` walks the prototype chain, so keys like
  // 'constructor' or 'toString' would pass the guard.
  return Object.prototype.hasOwnProperty.call(
    organizationSettingsGroups,
    groupId,
  );
}

// Builds the ancestor chain [orgId, parentId, grandParentId, ...] from the
// in-memory organizations list. Cycle-guarded and depth-capped (consistent
// with getChildOrganizations). Does NOT include ROOT_ORGANIZATION_SETTINGS_ID.
export function buildOrganizationChain(
  orgId: string,
  organizations: Array<{ id: string; parentId?: string | null }>,
  maxDepth = 10,
): string[] {
  const byId = new Map(organizations.map((org) => [org.id, org]));
  const chain: string[] = [];
  const visited = new Set<string>();

  let currentId: string | null | undefined = orgId;
  while (currentId && !visited.has(currentId) && chain.length < maxDepth) {
    visited.add(currentId);
    chain.push(currentId);
    currentId = byId.get(currentId)?.parentId;
  }

  return chain;
}

// The full resolution chain, nearest first: the org, its ancestors,
// then the "(root) settings".
export function buildSettingsResolutionChain(
  orgId: string,
  organizations: Array<{ id: string; parentId?: string | null }>,
): string[] {
  return [
    ...buildOrganizationChain(orgId, organizations),
    ROOT_ORGANIZATION_SETTINGS_ID,
  ];
}

// Nearest-doc-wins resolution for one group.
// `chainDocsNearestFirst` are the group's stored docs along the resolution
// chain (missing docs as null/undefined), ordered org → ... → (root).
// The first present doc fully defines the group; spreading over defaults
// only exists so that fields ADDED to a group later resolve without a data
// migration.
export function resolveSettingsFromChain<K extends OrganizationSettingsGroupId>(
  groupId: K,
  chainDocsNearestFirst: Array<
    | (OrganizationSettingsValues[K] & OrganizationSettingsAudit)
    | null
    | undefined
  >,
): OrganizationSettingsValues[K] {
  const defaults = organizationSettingsDefaults[groupId];
  for (const doc of chainDocsNearestFirst) {
    if (doc) {
      // Strip doc metadata (normalizeDoc-injected id + audit fields) — they
      // are not settings and must never round-trip into a saved doc.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, updatedAt, updatedBy, ...values } =
        doc as OrganizationSettingsValues[K] & OrganizationSettingsAudit;
      return {
        ...defaults,
        ...(values as Partial<OrganizationSettingsValues[K]>),
      };
    }
  }
  return { ...defaults };
}
